import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ArrowLeft, Loader2, RefreshCw, UploadCloud, ExternalLink, FileText, Sparkles } from "lucide-react";
import { tarifasApi, errMsg, fmtDate, pollRun } from "./api";
import { CorrespondenceList } from "./CorrespondenceList";
import { ProposalCard } from "./ProposalCard";
import { ReviewActions } from "./ReviewActions";
import { ApplyDialog } from "./ApplyDialog";
import { UnmatchedKimkim } from "./UnmatchedKimkim";

export const FileDetail = ({ fileId, onBack }) => {
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [selected, setSelected] = useState({});
  const [applyRun, setApplyRun] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const d = await tarifasApi.fileDetail(fileId);
      setData(d);
      if (d.provider) setActivities(await tarifasApi.activities({ provider_id: d.provider.id, all: true }));
    } catch (e) {
      toast.error(errMsg(e, "No se pudo cargar el archivo"));
    }
  }, [fileId]);

  useEffect(() => { load(); }, [load]);

  const lookup = useCallback(async (ref) => {
    const a = await tarifasApi.lookup(ref, data?.provider?.id);
    setActivities((prev) => [...prev.filter((x) => x.id !== a.id), a].sort((x, y) => x.title.localeCompare(y.title)));
    return a;
  }, [data]);

  // Polling mientras el archivo se esté procesando / re-emparejando
  useEffect(() => {
    if (data?.file?.status !== "processing") return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [data, load]);

  const rematch = async () => {
    if (!window.confirm("Se volverán a proponer correspondencias con el catálogo KimKim actual para las actividades aún no confirmadas ni rechazadas. ¿Continuar?")) return;
    setBusy("rematch");
    try {
      await tarifasApi.rematch(fileId);
      toast.success("Re-emparejando con IA… (puede tardar 1-2 min)");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo re-emparejar"));
    } finally {
      setBusy("");
    }
  };

  const recompute = async () => {
    setBusy("recompute");
    try {
      setData(await tarifasApi.recompute(fileId));
      toast.success("Propuestas de precio recalculadas");
    } catch (e) {
      toast.error(errMsg(e, "No se pudo recalcular"));
    } finally {
      setBusy("");
    }
  };

  const selectedKey = Object.keys(selected).filter((k) => selected[k]).sort().join(",");
  const selectedIds = useMemo(() => (selectedKey ? selectedKey.split(",") : []), [selectedKey]);
  const openApply = () => { if (selectedIds.length) setApplyOpen(true); };

  const apply = async () => {
    const ids = selectedIds;
    if (!ids.length) return;
    setBusy("apply");
    try {
      const run = await tarifasApi.apply(fileId, ids);
      setApplyOpen(false);
      setApplyRun(run);
      const done = await pollRun(run.id, setApplyRun);
      const s = done.summary || {};
      if (done.status === "done") toast.success(`KimKim: ${s.updated || 0} actualizadas · ${s.no_change || 0} sin cambios · ${s.failed || 0} fallidas`);
      else toast.error(done.message || "La actualización falló");
      setSelected({});
      await load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo lanzar la actualización"));
    } finally {
      setBusy("");
    }
  };

  if (!data) return <div className="py-8 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>;
  const { file, provider, extraction, correspondences, proposals } = data;
  const doc = extraction?.document || {};
  const ready = proposals.filter((p) => p.status === "ready");
  const counts = proposals.reduce((acc, p) => ({ ...acc, [p.status]: (acc[p.status] || 0) + 1 }), {});
  const nSelected = Object.values(selected).filter(Boolean).length;
  const toggleAll = () => {
    const all = ready.every((p) => selected[p.id]);
    setSelected(Object.fromEntries(ready.map((p) => [p.id, !all])));
  };

  return (
    <div className="space-y-4" data-testid="file-detail">
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="file-detail-back" variant="outline" size="sm" className="rounded-md" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" /> Volver</Button>
        <FileText className="h-5 w-5 text-muted-foreground" />
        <div>
          <h3 className="font-heading text-lg font-bold flex items-center gap-2">{file.name}
            {file.web_view_link && <a href={file.web_view_link} target="_blank" rel="noreferrer" className="text-muted-foreground"><ExternalLink className="h-4 w-4" /></a>}
          </h3>
          <p className="text-xs text-muted-foreground">{file.path} · proveedor <strong>{provider?.name}</strong> · versión {file.version} · procesado {fmtDate(file.processed_at)} · {extraction?.model}</p>
        </div>
      </div>

      <Card className="rounded-md">
        <CardContent className="p-4 grid gap-2 md:grid-cols-4 text-sm" data-testid="extraction-summary">
          <div><p className="text-xs text-muted-foreground">Documento</p><p className="font-medium">{doc.title || "—"}</p></div>
          <div><p className="text-xs text-muted-foreground">Vigencia</p><p className="font-medium">{doc.year || "—"} {doc.valid_from ? `· ${doc.valid_from} → ${doc.valid_to || "?"}` : ""}</p></div>
          <div><p className="text-xs text-muted-foreground">Tipo de precios</p><p className="font-medium">{doc.price_basis === "net" ? "Netos" : doc.price_basis === "public" ? `Públicos${doc.agency_discount_pct != null ? ` (−${doc.agency_discount_pct} % agencia)` : ""}` : provider?.price_basis === "net" ? "Netos (según ficha del proveedor)" : provider?.price_basis === "public" ? `Públicos (según ficha del proveedor${provider.agency_discount_pct != null ? `, −${provider.agency_discount_pct} %` : ""})` : "No indicado"} · {doc.currency || "—"} · impuestos {doc.taxes_included === true ? "incluidos" : doc.taxes_included === false ? "NO incluidos" : "sin indicar"}</p></div>
          <div><p className="text-xs text-muted-foreground">Actividades extraídas</p><p className="font-medium">{extraction?.activities?.length || 0} · {extraction?.activities?.reduce((n, a) => n + a.price_rows.length, 0) || 0} filas de precio</p></div>
          {doc.general_notes && <p className="md:col-span-4 text-xs text-muted-foreground italic">{doc.general_notes}</p>}
        </CardContent>
      </Card>

      <Card className="rounded-md">
        <CardHeader className="pb-1 flex flex-row flex-wrap items-start justify-between gap-2">
          <div>
            <h4 className="font-heading text-base font-bold">1. Correspondencias proveedor → KimKim</h4>
            <p className="text-xs text-muted-foreground">Revisa cada emparejamiento con su precio actual y el nuevo (neto + 8 %). Solo las confirmadas pasan al paso 2. Las confirmadas se reutilizan en futuros tarifarios.</p>
          </div>
          <Button data-testid="rematch-button" size="sm" variant="outline" className="rounded-md" onClick={rematch} disabled={busy === "rematch" || file.status === "processing"}>
            {busy === "rematch" || file.status === "processing" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />} Volver a emparejar con IA
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {file.status === "processing" && <p className="text-xs text-violet-700 pb-2" data-testid="file-processing-message">{file.status_message || "Procesando…"}</p>}
          <UnmatchedKimkim file={file} provider={provider} extraction={extraction} unmatched={data.unmatched_kimkim} onChanged={load} />
          <CorrespondenceList file={file} provider={provider} extraction={extraction} correspondences={correspondences} activities={activities} onChanged={load} onLookup={lookup} />
        </CardContent>
      </Card>

      <Card className="rounded-md">
        <CardHeader className="pb-1 flex flex-row flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="font-heading text-base font-bold">2. Precios propuestos (neto × 1,08, redondeado al euro superior)</h4>
            <p className="text-xs text-muted-foreground">
              {Object.entries(counts).map(([k, v]) => `${v} ${({ ready: "listas", no_change: "sin cambios", pending: "pendientes", applied: "aplicadas", failed: "fallidas", applying: "en curso" })[k] || k}`).join(" · ") || "Sin propuestas: confirma correspondencias y recalcula."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="recompute-button" size="sm" variant="outline" className="rounded-md" onClick={recompute} disabled={busy === "recompute"}>
              {busy === "recompute" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />} Recalcular
            </Button>
            {ready.length > 0 && <Button data-testid="select-all-ready" size="sm" variant="ghost" className="rounded-md" onClick={toggleAll}>{ready.every((p) => selected[p.id]) ? "Quitar selección" : `Seleccionar las ${ready.length} listas`}</Button>}
            <Button data-testid="apply-button" size="sm" className="rounded-md" onClick={openApply} disabled={!nSelected || busy === "apply"}>
              {busy === "apply" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <UploadCloud className="h-3 w-3 mr-1" />} Actualizar en KimKim ({nSelected})
            </Button>
          </div>
        </CardHeader>
        <ApplyDialog fileId={fileId} proposalIds={selectedIds} open={applyOpen} onClose={() => setApplyOpen(false)} onConfirm={apply} applying={busy === "apply"} />
        <CardContent className="pt-2 space-y-3">
          {applyRun && applyRun.status !== "done" && (
            <div className="rounded-md bg-violet-50 border border-violet-200 p-2 text-xs text-violet-900" data-testid="apply-progress">{applyRun.message || "Actualizando…"}</div>
          )}
          {applyRun?.status === "done" && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs" data-testid="apply-result">
              <p className="font-semibold mb-1">Resultado: {applyRun.summary?.updated || 0} actualizadas · {applyRun.summary?.no_change || 0} sin cambios · {applyRun.summary?.failed || 0} fallidas</p>
              <ul className="space-y-0.5">
                {(applyRun.items || []).map((it) => (
                  <li key={it.proposal_id} className="flex gap-2"><Badge className={`rounded-md ${it.result === "failed" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"} hover:bg-inherit`}>{it.result}</Badge> {it.kimkim_title} — {it.detail}</li>
                ))}
              </ul>
            </div>
          )}
          {data?.provider && <ReviewActions provider={data.provider} issues={[...new Set(proposals.flatMap((p) => p.issues || []))]} onFixed={recompute}
            taxHint={data.extraction?.document?.tax_pct ?? (data.extraction?.activities || []).flatMap((a) => a.price_rows || []).find((r) => r.tax_pct != null)?.tax_pct ?? null} />}
          {proposals.length === 0 && <p className="text-sm text-muted-foreground py-2" data-testid="proposals-empty">No hay propuestas todavía.</p>}
          {proposals.map((p) => <ProposalCard key={p.id} p={p} selected={selected[p.id]} onToggle={(id) => setSelected({ ...selected, [id]: !selected[id] })} />)}
        </CardContent>
      </Card>
    </div>
  );
};
